import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../client/components/ui/dialog";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import { Textarea } from "../../client/components/ui/textarea";
import { Switch } from "../../client/components/ui/switch";
import { Label } from "../../client/components/ui/label";
import { Copy } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { useToast } from "../../client/hooks/use-toast";
import { trackLayoutShared } from "../../client/utils/posthog";
import type { LayoutSummary } from "../operations";

export interface LayoutShareSettingsDialogProps {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  layout: Pick<LayoutSummary, "id" | "name" | "shareToken" | "isShared"> & {
    shareDescription?: string | null;
    shareDisplayName?: string | null;
  };
  onSave: (opts: {
    enabled: boolean;
    shareInLibrary?: boolean;
    shareDescription?: string;
    shareDisplayName?: string;
  }) => Promise<string | null>;
}

export default function LayoutShareSettingsDialog({ open, onOpenChange, layout, onSave }: LayoutShareSettingsDialogProps) {
  const t = useTranslation();
  const { toast } = useToast();

  const [token, setToken] = useState<string | null>(layout.shareToken);
  const [shareInLibrary, setShareInLibrary] = useState(layout.isShared);
  const [displayName, setDisplayName] = useState(layout.shareDisplayName ?? layout.name);
  const [description, setDescription] = useState(layout.shareDescription ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setToken(layout.shareToken);
      setShareInLibrary(layout.isShared);
      setDisplayName(layout.shareDisplayName ?? layout.name);
      setDescription(layout.shareDescription ?? "");
    }
  }, [open, layout]);

  const linkEnabled = token !== null;
  const url = token ? `${window.location.origin}/dashboard/layouts/share/${token}` : "";

  async function handleLinkToggle(checked: boolean) {
    setBusy(true);
    const result = await onSave({ enabled: checked, shareInLibrary: checked ? shareInLibrary : false });
    setToken(result);
    if (!checked) setShareInLibrary(false);
    if (checked) trackLayoutShared();
    setBusy(false);
  }

  async function handleSave() {
    setBusy(true);
    await onSave({
      enabled: linkEnabled,
      shareInLibrary,
      shareDescription: description,
      shareDisplayName: displayName,
    });
    toast({ description: t.dashboard.layouts.shareSettingsSaved, duration: 2000 });
    setBusy(false);
    onOpenChange(false);
  }

  function handleCopy() {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast({ description: t.dashboard.layouts.linkCopied, duration: 1500 });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.dashboard.layouts.shareDialogTitle.replace("{name}", layout.name)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Toggle lien */}
          <div className="flex items-center justify-between">
            <Label className="text-sm">{t.dashboard.layouts.shareToggle}</Label>
            <Switch checked={linkEnabled} disabled={busy} onCheckedChange={handleLinkToggle} />
          </div>

          {linkEnabled && (
            <>
              <div className="flex gap-2">
                <Input value={url} readOnly className="text-xs" />
                <Button size="sm" variant="outline" onClick={handleCopy}>
                  <Copy className="h-4 w-4 mr-1" />
                  {t.dashboard.layouts.copyLink}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">{t.dashboard.layouts.shareWarning}</p>

              {/* Toggle bibliothèque communautaire */}
              <div className="flex items-center justify-between pt-2 border-t border-border/50">
                <Label className="text-sm">{t.dashboard.layouts.shareLibraryToggle}</Label>
                <Switch checked={shareInLibrary} disabled={busy} onCheckedChange={(checked) => setShareInLibrary(checked)} />
              </div>

              {/* Nom public */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t.dashboard.layouts.shareDisplayNameLabel}</Label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t.dashboard.layouts.shareDescriptionLabel}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder={t.dashboard.layouts.shareDescriptionPlaceholder}
                />
                <p className="text-xs text-muted-foreground text-right">{description.length}/500</p>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t.common.cancel}</Button>
          <Button onClick={handleSave} disabled={busy}>{t.common.save}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
